import type {
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
} from "vue-router";
import { useRouter, useRoute } from "vue-router";
import { computed } from "vue";
import type {
  INavigationParamsRegister,
  INavigationQueryRegister,
} from "./register";

export type { INavigationParamsRegister } from "./register";
export type { INavigationRouteName } from "./route-name";

export const useNavigation = () => {
  const vueRouter = useRouter();
  const vueRoute = useRoute();

  const route = computed<RouteLocationNormalizedLoaded>(() => vueRoute);

  const push = <T extends keyof INavigationParamsRegister>(
    name: T,
    params: INavigationParamsRegister[T],
    query: INavigationQueryRegister[T] = {},
  ) => {
    return vueRouter.push({
      name,
      params,
      query,
    });
  };

  const goToItem = (itemId: string) => {
    return vueRouter.push({
      query: {
        "item-id": itemId,
      },
    });
  };

  const goToEditItem = (itemId: string, lineId: string) => {
    return vueRouter.push({
      query: {
        ...route.value.query,
        "item-id": itemId,
        "edit-line-id": lineId,
      },
    });
  };

  const rawPush = (route: RouteLocationRaw) => {
    vueRouter.push(route);
  };

  const rawReplace = (route: RouteLocationRaw) => {
    vueRouter.replace(route);
  };

  const replace = <T extends keyof INavigationParamsRegister>(
    name: T,
    params: INavigationParamsRegister[T],
    query: INavigationQueryRegister[T] = {},
  ) => {
    vueRouter.replace({
      name,
      params,
      query,
    });
  };

  const back = () => {
    if (vueRouter.options.history.state.back) {
      vueRouter.back();
      return;
    }

    push("order", {});
  };

  const resolvePath = <T extends keyof INavigationParamsRegister>(
    name: T,
    params: INavigationParamsRegister[T],
    query: INavigationQueryRegister[T] = {},
  ) => {
    return vueRouter.resolve({
      name,
      params,
      query,
    }).fullPath;
  };

  return {
    route,
    back,
    push,
    goToItem,
    goToEditItem,
    rawPush,
    replace,
    rawReplace,
    resolvePath,
  };
};
